import { createContext, useContext, useState, useEffect, ReactNode } from "react";

export interface BrandingConfig {
  companyName: string;
  platformName: string;
  tagline: string;
  primaryHue: number;
  primarySaturation: number;
  primaryLightness: number;
  accentHue: number;
  accentSaturation: number;
  accentLightness: number;
  logoUrl: string | null;
  darkMode: boolean;
}

const CCC_BLUE_DEFAULT: BrandingConfig = {
  companyName: "Creative Concepts Contracting",
  platformName: "Koredor",
  tagline: "Contractor Operating System",
  // Detroit Lions / CCC Blue: hsl(207, 80%, 42%)
  primaryHue: 207,
  primarySaturation: 80,
  primaryLightness: 42,
  // Accent: warm amber-orange
  accentHue: 24,
  accentSaturation: 90,
  accentLightness: 52,
  logoUrl: null,
  darkMode: true,
};

interface BrandingContextValue {
  branding: BrandingConfig;
  setBranding: (b: BrandingConfig) => void;
}

const BrandingContext = createContext<BrandingContextValue | null>(null);

function applyBrandingCSS(b: BrandingConfig) {
  const root = document.documentElement;
  const p = `${b.primaryHue} ${b.primarySaturation}% ${b.primaryLightness}%`;
  const pDark = `${b.primaryHue} ${b.primarySaturation}% ${b.primaryLightness + 10}%`;
  const a = `${b.accentHue} ${b.accentSaturation}% ${b.accentLightness}%`;

  root.style.setProperty("--primary", p);
  root.style.setProperty("--ring", p);
  root.style.setProperty("--accent", a);
  root.style.setProperty("--sidebar-primary", a);
  root.style.setProperty("--sidebar-ring", a);
  root.style.setProperty("--platform-glow", p);

  if (b.darkMode) {
    root.classList.add("dark");
    root.style.setProperty("--primary", pDark);
    root.style.setProperty("--ring", pDark);
  } else {
    root.classList.remove("dark");
  }
}

const STORAGE_KEY = "koredor_branding";

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [branding, setBrandingState] = useState<BrandingConfig>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? { ...CCC_BLUE_DEFAULT, ...JSON.parse(saved) } : CCC_BLUE_DEFAULT;
    } catch {
      return CCC_BLUE_DEFAULT;
    }
  });

  const setBranding = (b: BrandingConfig) => {
    setBrandingState(b);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(b));
  };

  useEffect(() => {
    applyBrandingCSS(branding);
  }, [branding]);

  return (
    <BrandingContext.Provider value={{ branding, setBranding }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  const ctx = useContext(BrandingContext);
  if (!ctx) throw new Error("useBranding must be used within BrandingProvider");
  return ctx;
}

export { CCC_BLUE_DEFAULT };
